/* Shopify Ajax predictive search. No requests before a visitor types 3 characters. */
(() => {
  'use strict';
  const cache = new Map();
  const normalise = (value) => value.trim().replace(/\s+/g, ' ').normalize('NFC');
  const enough = (value) => Array.from(value).length >= 3;
  const imageSource = (value) => {
    if (typeof value === 'string') return value;
    if (value && typeof value.url === 'string') return value.url;
    if (value && typeof value.src === 'string') return value.src;
    return '';
  };
  const safeURL = (value) => {
    try {
      const url = new URL(value, location.origin);
      return /^(https?:)$/.test(url.protocol) ? url : null;
    } catch { return null; }
  };
  const init = (scope = document) => scope.querySelectorAll('[data-header-search]').forEach((root) => {
    if (root.dataset.searchReady) return;
    root.dataset.searchReady = 'true';
    const input = root.querySelector('[data-header-search-input]');
    const form = root.querySelector('form');
    const panel = root.querySelector('[data-header-search-panel]');
    const results = root.querySelector('[data-header-search-results]');
    const status = root.querySelector('[data-header-search-status]');
    const all = root.querySelector('[data-header-search-all]');
    const menu = root.closest('header')?.querySelector('.rh-header-menu');
    if (menu) {
      // Restore focus before the theme's shared Escape handler closes details.
      menu.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && menu.open) {
          menu.open = false;
          menu.querySelector('summary')?.focus();
        }
      });
      document.addEventListener('pointerdown', (event) => {
        if (menu.open && !menu.contains(event.target)) menu.open = false;
      });
    }
    let timer, controller, active = -1, revision = 0, composing = false;
    const options = () => Array.from(results.querySelector('[role="option"]') ? results.querySelectorAll('[role="option"]') : []);
    const select = (index) => {
      const links = options();
      active = links.length ? (index + links.length) % links.length : -1;
      links.forEach((link, i) => link.setAttribute('aria-selected', String(i === active)));
      if (active < 0) input.removeAttribute('aria-activedescendant');
      else {
        input.setAttribute('aria-activedescendant', links[active].id);
        links[active].scrollIntoView({ block: 'nearest' });
      }
    };
    const cancel = () => { clearTimeout(timer); controller?.abort(); revision += 1; };
    const close = () => {
      cancel();
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      results.setAttribute('aria-busy', 'false');
      active = -1;
    };
    const show = () => {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };
    const updateAll = (query) => {
      const url = new URL(form.action, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      all.href = url.href;
    };
    const create = (tag, className, text) => {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text) element.textContent = text;
      return element;
    };
    const render = (data, query) => {
      results.replaceChildren();
      let count = 0;
      [['collections', 'Categories'], ['products', 'Places']].forEach(([key, label]) => {
        const items = Array.isArray(data[key]) ? data[key].slice(0, 5) : [];
        if (!items.length) return;
        const group = create('div', 'rh-header-search__group');
        const heading = create('div', 'rh-header-search__heading', label);
        heading.id = `${input.id}-${key}`;
        group.setAttribute('role', 'group');
        group.setAttribute('aria-labelledby', heading.id);
        group.append(heading);
        items.forEach((item) => {
          const url = safeURL(item.url);
          if (!url || url.origin !== location.origin || !item.title) return;
          const link = create('a', 'rh-header-search__item');
          link.href = url.href;
          link.id = `${input.id}-Option-${count++}`;
          link.setAttribute('role', 'option');
          link.setAttribute('aria-selected', 'false');
          const thumb = create('span', 'rh-header-search__thumb');
          thumb.setAttribute('aria-hidden', 'true');
          const imageValue = imageSource(item.featured_image) || imageSource(item.image);
          const imageURL = imageValue ? safeURL(imageValue) : null;
          if (imageURL) {
            imageURL.searchParams.set('width', '96');
            const image = create('img');
            image.src = imageURL.href;
            image.alt = '';
            image.width = 46;
            image.height = 46;
            image.loading = 'lazy';
            image.decoding = 'async';
            thumb.append(image);
          } else thumb.textContent = key === 'collections' ? '↗' : '⌂';
          const copy = create('span', 'rh-header-search__copy');
          copy.append(create('span', 'rh-header-search__name', item.title));
          copy.append(create('span', 'rh-header-search__type', key === 'collections' ? 'Category' : (item.type || 'Place')));
          const arrow = create('span', 'rh-header-search__arrow', '↗');
          arrow.setAttribute('aria-hidden', 'true');
          link.append(thumb, copy, arrow);
          group.append(link);
        });
        if (group.children.length > 1) results.append(group);
      });
      status.textContent = count ? `${count} suggestions for “${query}”.` : `No suggestions for “${query}”. Try another word or see all matching places.`;
      results.setAttribute('aria-busy', 'false');
      active = -1;
      input.removeAttribute('aria-activedescendant');
      show();
    };
    const search = async (query, token) => {
      const key = query.toLocaleLowerCase();
      if (cache.has(key)) { render(cache.get(key), query); return; }
      controller = new AbortController();
      const requestController = controller;
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; requestController.abort(); }, 8000);
      try {
        const url = new URL(root.dataset.predictiveUrl, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection');
        url.searchParams.set('resources[limit]', '5');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'show');
        const response = await fetch(url.href, { signal: requestController.signal, headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Search unavailable');
        const json = await response.json();
        const data = json.resources?.results;
        if (!data) throw new Error('Search unavailable');
        if (token !== revision || query !== normalise(input.value)) return;
        if (cache.size >= 24) cache.delete(cache.keys().next().value);
        cache.set(key, data);
        render(data, query);
      } catch (error) {
        if (token !== revision || (error.name === 'AbortError' && !timedOut)) return;
        results.replaceChildren();
        results.setAttribute('aria-busy', 'false');
        status.textContent = 'Suggestions are unavailable right now. Press Enter to search all places.';
        show();
      } finally { clearTimeout(timeout); }
    };
    const schedule = () => {
      cancel();
      const query = normalise(input.value);
      results.replaceChildren();
      active = -1;
      input.removeAttribute('aria-activedescendant');
      if (composing || !enough(query)) { close(); return; }
      updateAll(query);
      status.textContent = 'Looking for matching places and categories…';
      results.setAttribute('aria-busy', 'true');
      show();
      const token = revision;
      timer = setTimeout(() => search(query, token), 250);
    };
    input.addEventListener('input', schedule);
    input.addEventListener('compositionstart', () => { composing = true; close(); });
    input.addEventListener('compositionend', () => { composing = false; schedule(); });
    input.addEventListener('focus', () => { if (enough(normalise(input.value))) schedule(); });
    input.addEventListener('keydown', (event) => {
      if (event.isComposing) return;
      if (event.key === 'Escape') { close(); return; }
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !panel.hidden && options().length) {
        event.preventDefault();
        select(active < 0 ? (event.key === 'ArrowDown' ? 0 : options().length - 1) : active + (event.key === 'ArrowDown' ? 1 : -1));
      }
      if (event.key === 'Enter' && active >= 0 && !panel.hidden) {
        const link = options()[active];
        if (link) { event.preventDefault(); link.click(); }
      }
    });
    form.addEventListener('submit', (event) => {
      if (!normalise(input.value)) { event.preventDefault(); input.focus(); }
    });
    root.addEventListener('focusout', (event) => { if (!root.contains(event.relatedTarget)) close(); });
    document.addEventListener('pointerdown', (event) => { if (!root.contains(event.target)) close(); });
    document.addEventListener('shopify:section:unload', (event) => { if (event.target.contains(root)) close(); });
  });
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
